/* ============================================
   Darya Natural Glow - Exit Intent Popup Logic
   Smart triggers + tracking for Meta & GA4
   ============================================ */

(function() {
  'use strict';

  const popup = document.getElementById('runperf-exit-popup');
  if (!popup) return;

  // ===== Config (from data attributes) =====
  const TRIGGER_MODE = popup.dataset.triggerMode || 'exit_only'; // exit_only | time_only | both
  const MIN_DELAY_MS = (parseInt(popup.dataset.delay, 10) || 15) * 1000;
  const DISCOUNT_CODE = popup.dataset.code || 'WELCOME5';
  const COUNTDOWN_MINUTES = parseInt(popup.dataset.countdown, 10) || 10;
  const STORAGE_KEY = 'runperf_exit_popup_shown';
  const CODE_USED_KEY = 'runperf_exit_code_used';

  // ===== Exclusion checks (performance marketer logic) =====

  // 1. Don't show on checkout / cart / account pages - never interrupt buying intent
  const path = window.location.pathname.toLowerCase();
  const excludedPaths = ['/checkout', '/cart', '/account', '/orders', '/policies'];
  if (excludedPaths.some(p => path.startsWith(p))) return;

  // 2. Don't show if user already saw it (localStorage - 7 days)
  try {
    const lastShown = localStorage.getItem(STORAGE_KEY);
    if (lastShown) {
      const daysAgo = (Date.now() - parseInt(lastShown, 10)) / (1000 * 60 * 60 * 24);
      if (daysAgo < 7) return;
    }
  } catch(e) { /* localStorage blocked, continue */ }

  // 3. Don't show if user already used/copied the code
  try {
    if (localStorage.getItem(CODE_USED_KEY)) return;
  } catch(e) {}

  // 4. Don't show if user has items in cart (protect conversion)
  function hasItemsInCart() {
    try {
      const cartCount = document.querySelector('[data-cart-count], .cart-count, #CartCount, .cart-link__bubble');
      if (cartCount) {
        const text = cartCount.textContent || cartCount.innerText || '0';
        const count = parseInt(text.trim(), 10);
        if (!isNaN(count) && count > 0) return true;
      }
    } catch(e) {}
    return false;
  }

  // ===== State =====
  let popupShown = false;
  let minDelayPassed = false;
  let countdownInterval = null;
  let timeBasedTimer = null;

  // ===== Minimum delay gate =====
  setTimeout(() => { 
    minDelayPassed = true;
    
    // If trigger mode is time_only or both, show after delay
    if (TRIGGER_MODE === 'time_only' || TRIGGER_MODE === 'both') {
      showPopup(TRIGGER_MODE === 'time_only' ? 'time_based' : 'time_fallback');
    }
  }, MIN_DELAY_MS);

  // ===== Show popup =====
  function showPopup(trigger) {
    if (popupShown) return;
    if (!minDelayPassed) return; // respect minimum delay always
    if (hasItemsInCart()) return; // re-check at show time

    popupShown = true;
    popup.classList.add('is-active');
    popup.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    try { localStorage.setItem(STORAGE_KEY, Date.now().toString()); } catch(e) {}

    startCountdown();
    trackEvent('popup_shown', { trigger: trigger, code: DISCOUNT_CODE });
  }

  // ===== Close popup =====
  function closePopup(reason) {
    popup.classList.remove('is-active');
    popup.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (countdownInterval) clearInterval(countdownInterval);
    trackEvent('popup_closed', { reason: reason || 'close_button' });
  }

  // ===== Exit-intent detection =====
  // Only bind if trigger mode includes exit intent
  if (TRIGGER_MODE === 'exit_only' || TRIGGER_MODE === 'both') {
    
    // Desktop: mouse leaves from top of viewport (going to close tab / address bar)
    function onMouseLeave(e) {
      // Must leave upward and exit viewport from top
      if (e.clientY <= 5 && e.relatedTarget === null) {
        showPopup('exit_intent_desktop');
      }
    }
    document.addEventListener('mouseout', onMouseLeave);

    // Mobile: back button trigger (best real exit signal on mobile)
    function setupBackButtonTrigger() {
      if (window.innerWidth > 768) return;
      history.pushState({ runperfPopup: true }, '');
      window.addEventListener('popstate', function(e) {
        if (!popupShown && minDelayPassed) {
          history.pushState({ runperfPopup: true }, '');
          showPopup('back_button_mobile');
        }
      });
    }
    setupBackButtonTrigger();

    // Mobile: fast scroll to top (often precedes exit)
    let lastScrollY = window.scrollY;
    let lastScrollTime = Date.now();
    function onScroll() {
      if (window.innerWidth > 768) return;
      const currentY = window.scrollY;
      const currentTime = Date.now();
      const deltaY = lastScrollY - currentY;
      const deltaTime = currentTime - lastScrollTime;

      if (deltaY > 150 && deltaTime < 400 && currentY < 150) {
        showPopup('scroll_up_mobile');
      }
      lastScrollY = currentY;
      lastScrollTime = currentTime;
    }
    window.addEventListener('scroll', throttle(onScroll, 150), { passive: true });

    // Tab switching / blur (user about to leave)
    let blurStartTime = null;
    window.addEventListener('blur', () => { blurStartTime = Date.now(); });
    window.addEventListener('focus', () => {
      // If they came back quickly (<500ms), ignore. If longer, they were checking out.
      // We don't fire on focus because user is back - we just use this to not fire
      blurStartTime = null;
    });
  }

  // ===== Copy code to clipboard =====
  function copyCode() {
    const copyBtn = popup.querySelector('[data-copy-code]');
    const defaultText = copyBtn.querySelectorAll('[data-copy-default]');
    const successText = copyBtn.querySelectorAll('[data-copy-success]');

    const doCopy = () => {
      copyBtn.classList.add('is-copied');
      defaultText.forEach(el => el.style.display = 'none');
      successText.forEach(el => el.style.display = 'inline');

      setTimeout(() => {
        copyBtn.classList.remove('is-copied');
        defaultText.forEach(el => el.style.display = '');
        successText.forEach(el => el.style.display = 'none');
      }, 2500);

      try { localStorage.setItem(CODE_USED_KEY, 'copied'); } catch(e) {}
      trackEvent('popup_code_copied', { code: DISCOUNT_CODE });
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(DISCOUNT_CODE).then(doCopy).catch(() => {
        fallbackCopy(DISCOUNT_CODE); doCopy();
      });
    } else {
      fallbackCopy(DISCOUNT_CODE); doCopy();
    }
  }

  function fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try { document.execCommand('copy'); } catch(e) {}
    document.body.removeChild(textarea);
  }

  // ===== Countdown timer =====
  function startCountdown() {
    const timerEl = popup.querySelector('[data-timer]');
    if (!timerEl) return;

    let totalSeconds = COUNTDOWN_MINUTES * 60;

    const update = () => {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      timerEl.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      if (totalSeconds <= 0) { clearInterval(countdownInterval); timerEl.textContent = '00:00'; }
      totalSeconds--;
    };

    update();
    countdownInterval = setInterval(update, 1000);
  }

  // ===== Tracking (Meta Pixel + GA4 + dataLayer) =====
  function trackEvent(eventName, data) {
    if (typeof fbq === 'function') {
      fbq('trackCustom', 'RunPerfExitPopup_' + eventName, data);
    }
    if (typeof gtag === 'function') {
      gtag('event', 'exit_popup_' + eventName, data);
    }
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'runperf_exit_popup_' + eventName, ...data });
    }
  }

  // ===== Close handlers =====
  popup.querySelectorAll('[data-close]').forEach(el => {
    el.addEventListener('click', () => closePopup('user_dismissed'));
  });

  // Copy button
  const copyBtn = popup.querySelector('[data-copy-code]');
  if (copyBtn) copyBtn.addEventListener('click', copyCode);

  // CTA tracking
  const ctaBtn = popup.querySelector('[data-cta]');
  if (ctaBtn) {
    ctaBtn.addEventListener('click', () => {
      trackEvent('popup_cta_clicked', { code: DISCOUNT_CODE });
    });
  }

  // ESC key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && popup.classList.contains('is-active')) {
      closePopup('escape_key');
    }
  });

  // ===== Utilities =====
  function throttle(fn, wait) {
    let lastCall = 0;
    return function(...args) {
      const now = Date.now();
      if (now - lastCall >= wait) {
        lastCall = now;
        fn.apply(this, args);
      }
    };
  }

})();
